'use client';

import type { DateOption } from '@/packages/sxm/sports/schedule';
import { ALL_CONFERENCES } from '@/packages/sxm/sports/conferences';
import { Option } from '@/ui-components/atoms/Inputs/Select/Option';
import { OptionGroup } from '@/ui-components/atoms/Inputs/Select/OptionGroup';
import { Select } from '@/ui-components/atoms/Inputs/Select/Select';
import styles from './sportschedule.module.scss';

interface ScheduleFiltersProps {
    itemLabel: string;
    conferenceLabel: string;
    dateOptions: DateOption[];
    dateValue?: string;
    dateLabel?: string;
    onDateChange: (value: string) => void;
    conferenceOptions: string[];
    conference: string;
    onConferenceChange: (conference: string) => void;
}

function ConferenceFilter({
    conferenceLabel,
    conferenceOptions,
    conference,
    onConferenceChange,
}: Pick<ScheduleFiltersProps, 'conferenceLabel' | 'conferenceOptions' | 'conference' | 'onConferenceChange'>) {
    if (!conferenceOptions.length) return undefined;

    const allLabel = `All ${conferenceLabel}s`;
    const placeholders: Record<string, string> = { [ALL_CONFERENCES]: allLabel };
    return (
        <Select
            label={`filter games by ${conferenceLabel}`}
            type="bar"
            value={conference}
            placeholder={placeholders[conference] ?? conference}
            name="confFilter"
            onChange={(element) => onConferenceChange(element.value)}
            className={styles.filterBarSelect}
        >
            <OptionGroup>
                <Option value={ALL_CONFERENCES}>{allLabel}</Option>
                {conferenceOptions.map((option) => (
                    <Option key={option} value={option}>
                        {option}
                    </Option>
                ))}
            </OptionGroup>
        </Select>
    );
}

function DateFilter({
    itemLabel,
    dateOptions,
    dateValue,
    dateLabel,
    onDateChange,
}: Pick<ScheduleFiltersProps, 'itemLabel' | 'dateOptions' | 'dateValue' | 'dateLabel' | 'onDateChange'>) {
    if (!dateOptions.length) return undefined;

    return (
        <Select
            label={`filter ${itemLabel} by date`}
            type="bar"
            value={dateValue}
            placeholder={dateLabel}
            name="dateFilter"
            onChange={(element) => onDateChange(element.value)}
            className={styles.filterBarSelect}
        >
            <OptionGroup>
                {dateOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
                <Option value="" disableOption className={styles.moreGames}>
                    More events will be added as information becomes available.
                </Option>
            </OptionGroup>
        </Select>
    );
}

export function ScheduleFilters(props: ScheduleFiltersProps) {
    return (
        <div className={styles.filterBar} data-cy="games-grid">
            <ConferenceFilter {...props} />
            <DateFilter {...props} />
        </div>
    );
}
